import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Music, Upload, Loader2, DollarSign, BarChart3, CheckCircle, Clock, XCircle, Link2, Headphones, Heart, Play, Users, Edit3, Save } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { useAuth } from "../App";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProducerPage = () => {
  const { user } = useAuth();
  const [producer, setProducer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [directory, setDirectory] = useState([]);
  const [dirLoading, setDirLoading] = useState(true);
  const [showApply, setShowApply] = useState(false);
  const [showSubmit, setShowSubmit] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [applying, setApplying] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dashboard, setDashboard] = useState(null);

  const [applyForm, setApplyForm] = useState({ artist_name: "", bio: "", portfolio_url: "", social_links: { instagram: "", youtube: "", soundcloud: "" } });
  const [beatFile, setBeatFile] = useState(null);
  const [beatImage, setBeatImage] = useState(null);
  const [beatForm, setBeatForm] = useState({ title: "", genre: "Trap", bpm: 140, key: "Am", tags: "", basic_price: 29.99, premium_price: 79.99, unlimited_price: 149.99, exclusive_price: 499.99 });
  const [editForm, setEditForm] = useState({ bio: "", tagline: "", portfolio_url: "", avatar_url: "", banner_url: "", social_links: { instagram: "", youtube: "", soundcloud: "" } });

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetchDirectory();
    if (user) fetchProducerProfile();
    else setLoading(false);
  }, [user]);

  const fetchDirectory = async () => {
    try {
      const res = await axios.get(`${API}/producers/directory`);
      setDirectory(res.data);
    } catch (e) {} finally { setDirLoading(false); }
  };

  const fetchProducerProfile = async () => {
    try {
      const res = await axios.get(`${API}/producers/me`, { headers: getAuthHeaders(), withCredentials: true });
      if (res.data.status !== "not_applied") {
        setProducer(res.data);
        if (res.data.status === "approved") {
          fetchDashboard();
          setEditForm({
            bio: res.data.bio || "",
            tagline: res.data.tagline || "",
            portfolio_url: res.data.portfolio_url || "",
            avatar_url: res.data.avatar_url || "",
            banner_url: res.data.banner_url || "",
            social_links: { instagram: "", youtube: "", soundcloud: "", ...(res.data.social_links || {}) }
          });
        }
      }
    } catch (e) {} finally { setLoading(false); }
  };

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`${API}/producers/dashboard`, { headers: getAuthHeaders(), withCredentials: true });
      setDashboard(res.data);
    } catch (e) {}
  };

  const handleApply = async (e) => {
    e.preventDefault();
    setApplying(true);
    try {
      const payload = { ...applyForm, social_links: Object.fromEntries(Object.entries(applyForm.social_links).filter(([_, v]) => v)) };
      const res = await axios.post(`${API}/producers/apply`, payload, { headers: getAuthHeaders(), withCredentials: true });
      setProducer(res.data);
      setShowApply(false);
      toast.success("Application submitted! We'll review it soon.");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to apply"); }
    finally { setApplying(false); }
  };

  const handleSubmitBeat = async (e) => {
    e.preventDefault();
    if (!beatFile) return;
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("audio_file", beatFile);
      if (beatImage) formData.append("image_file", beatImage);
      Object.entries(beatForm).forEach(([k, v]) => formData.append(k, v));
      await axios.post(`${API}/producers/beats/submit`, formData, {
        headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" }, withCredentials: true
      });
      toast.success("Beat submitted for review!");
      setShowSubmit(false);
      setBeatFile(null);
      setBeatImage(null);
      fetchDashboard();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to submit"); }
    finally { setSubmitting(false); }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...editForm,
        social_links: Object.fromEntries(Object.entries(editForm.social_links).filter(([_, v]) => v))
      };
      await axios.put(`${API}/producers/profile`, payload, { headers: getAuthHeaders(), withCredentials: true });
      toast.success("Profile updated!");
      setShowEdit(false);
      fetchProducerProfile();
    } catch (e) { toast.error("Failed to update profile"); }
    finally { setSaving(false); }
  };

  if (loading && dirLoading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-neon-cyan" /></div>;

  // Approved producer with dashboard + directory below
  const isApproved = producer?.status === "approved";
  const stats = dashboard?.stats || {};

  return (
    <div className="min-h-screen py-10 px-6" data-testid="producer-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <Badge className="bg-neon-cyan/20 text-neon-cyan border-neon-cyan/50 mb-4">
            <Headphones className="w-3 h-3 mr-1" />Producers
          </Badge>
          <h1 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mb-4">
            Producer Hub
          </h1>
          <p className="text-zinc-400 max-w-xl mx-auto">
            Discover talented producers, explore their catalogs, or apply to sell your beats on Gas Station.
          </p>
        </div>

        {/* CTA for non-producer users */}
        {user && !producer && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass p-8 md:p-10 mb-12 text-center border border-neon-cyan/20">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
              {[
                { icon: Upload, title: "Submit Beats", desc: "Upload your best work for review" },
                { icon: DollarSign, title: "Earn Revenue", desc: "Up to 50% revenue split per sale" },
                { icon: BarChart3, title: "Track Sales", desc: "Dashboard with real-time stats" },
              ].map(({ icon: Icon, title, desc }) => (
                <div key={title} className="text-center">
                  <Icon className="w-10 h-10 text-neon-cyan mx-auto mb-3" />
                  <h3 className="font-medium mb-1">{title}</h3>
                  <p className="text-xs text-zinc-500">{desc}</p>
                </div>
              ))}
            </div>
            <Dialog open={showApply} onOpenChange={setShowApply}>
              <DialogTrigger asChild>
                <Button className="bg-neon-cyan text-black font-bold text-lg px-10 py-6" data-testid="apply-producer-button">Apply Now</Button>
              </DialogTrigger>
              <DialogContent className="bg-zinc-900 border-white/10 max-w-lg max-h-[80vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-heading">Producer Application</DialogTitle></DialogHeader>
                <form onSubmit={handleApply} className="space-y-4">
                  <div><Label>Artist / Producer Name</Label><Input value={applyForm.artist_name} onChange={(e) => setApplyForm({...applyForm, artist_name: e.target.value})} className="bg-black/50 border-white/10" required placeholder="Your stage name" data-testid="producer-name-input" /></div>
                  <div><Label>Bio</Label><Textarea value={applyForm.bio} onChange={(e) => setApplyForm({...applyForm, bio: e.target.value})} className="bg-black/50 border-white/10" placeholder="Tell us about your music..." rows={3} /></div>
                  <div><Label>Portfolio URL</Label><Input value={applyForm.portfolio_url} onChange={(e) => setApplyForm({...applyForm, portfolio_url: e.target.value})} className="bg-black/50 border-white/10" placeholder="https://..." /></div>
                  <div><Label>Social Links</Label><div className="space-y-2 mt-1">{["instagram", "youtube", "soundcloud"].map(p => (<Input key={p} value={applyForm.social_links[p]} onChange={(e) => setApplyForm({...applyForm, social_links: {...applyForm.social_links, [p]: e.target.value}})} className="bg-black/50 border-white/10" placeholder={`${p.charAt(0).toUpperCase() + p.slice(1)} URL`} />))}</div></div>
                  <Button type="submit" disabled={applying} className="w-full bg-neon-cyan text-black font-bold" data-testid="submit-application">{applying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Submit Application</Button>
                </form>
              </DialogContent>
            </Dialog>
          </motion.div>
        )}

        {!user && (
          <div className="glass p-8 mb-12 text-center border border-neon-cyan/20">
            <p className="text-zinc-400">Sign in to apply as a producer and start selling your beats on Gas Station.</p>
          </div>
        )}

        {/* Pending state */}
        {producer?.status === "pending" && (
          <div className="glass p-8 mb-12 text-center border border-amber-500/20">
            <Clock className="w-10 h-10 text-amber-400 mx-auto mb-3" />
            <h2 className="font-heading text-xl uppercase mb-2">Application Pending</h2>
            <p className="text-zinc-400 text-sm">Your application as <strong className="text-neon-cyan">{producer.artist_name}</strong> is under review.</p>
          </div>
        )}

        {producer?.status === "rejected" && (
          <div className="glass p-8 mb-12 text-center border border-red-500/20">
            <XCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
            <h2 className="font-heading text-xl uppercase mb-2">Application Not Approved</h2>
            <p className="text-zinc-400 text-sm">Unfortunately your application wasn't approved this time.</p>
          </div>
        )}

        {/* Approved Producer Dashboard */}
        {isApproved && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-12 glass p-6 border border-neon-green/20">
            <div className="flex items-center justify-between mb-6">
              <div>
                <Badge className="bg-neon-green/20 text-neon-green border-neon-green/50 mb-1">Your Dashboard</Badge>
                <h2 className="font-heading text-xl uppercase">{producer.artist_name}</h2>
              </div>
              <div className="flex gap-2">
                <Link to={`/producers/${producer.producer_id}`}>
                  <Button variant="outline" size="sm" className="border-neon-cyan/50 text-neon-cyan" data-testid="view-storefront-btn">View Storefront</Button>
                </Link>
                <Dialog open={showEdit} onOpenChange={setShowEdit}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" className="border-white/10" data-testid="edit-profile-btn"><Edit3 className="w-4 h-4 mr-1" />Edit Profile</Button>
                  </DialogTrigger>
                  <DialogContent className="bg-zinc-900 border-white/10 max-w-lg max-h-[80vh] overflow-y-auto">
                    <DialogHeader><DialogTitle className="font-heading">Edit Storefront</DialogTitle></DialogHeader>
                    <form onSubmit={handleSaveProfile} className="space-y-4">
                      <div><Label>Tagline</Label><Input value={editForm.tagline} onChange={(e) => setEditForm({...editForm, tagline: e.target.value})} className="bg-black/50 border-white/10" placeholder="Your one-liner" /></div>
                      <div><Label>Bio</Label><Textarea value={editForm.bio} onChange={(e) => setEditForm({...editForm, bio: e.target.value})} className="bg-black/50 border-white/10" rows={3} /></div>
                      <div><Label>Avatar URL</Label><Input value={editForm.avatar_url} onChange={(e) => setEditForm({...editForm, avatar_url: e.target.value})} className="bg-black/50 border-white/10" placeholder="https://..." /></div>
                      <div><Label>Banner URL</Label><Input value={editForm.banner_url} onChange={(e) => setEditForm({...editForm, banner_url: e.target.value})} className="bg-black/50 border-white/10" placeholder="https://..." /></div>
                      <div><Label>Portfolio URL</Label><Input value={editForm.portfolio_url} onChange={(e) => setEditForm({...editForm, portfolio_url: e.target.value})} className="bg-black/50 border-white/10" /></div>
                      <div><Label>Social Links</Label><div className="space-y-2 mt-1">{["instagram", "youtube", "soundcloud"].map(p => (<Input key={p} value={editForm.social_links[p] || ""} onChange={(e) => setEditForm({...editForm, social_links: {...editForm.social_links, [p]: e.target.value}})} className="bg-black/50 border-white/10" placeholder={p.charAt(0).toUpperCase() + p.slice(1)} />))}</div></div>
                      <Button type="submit" disabled={saving} className="w-full bg-neon-cyan text-black font-bold" data-testid="save-profile-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Save Profile</Button>
                    </form>
                  </DialogContent>
                </Dialog>
                <Dialog open={showSubmit} onOpenChange={setShowSubmit}>
                  <DialogTrigger asChild>
                    <Button className="bg-neon-cyan text-black font-bold" data-testid="submit-beat-button"><Upload className="w-4 h-4 mr-2" />Submit Beat</Button>
                  </DialogTrigger>
                  <DialogContent className="bg-zinc-900 border-white/10 max-w-lg max-h-[80vh] overflow-y-auto">
                    <DialogHeader><DialogTitle className="font-heading">Submit a Beat</DialogTitle></DialogHeader>
                    <form onSubmit={handleSubmitBeat} className="space-y-4">
                      <div><Label>Audio File (MP3/WAV)</Label><Input type="file" accept="audio/*" onChange={(e) => setBeatFile(e.target.files[0])} className="bg-black/50 border-white/10" required data-testid="beat-audio-input" /></div>
                      <div><Label>Cover Image (optional)</Label><Input type="file" accept="image/*" onChange={(e) => setBeatImage(e.target.files[0])} className="bg-black/50 border-white/10" /></div>
                      <div><Label>Title</Label><Input value={beatForm.title} onChange={(e) => setBeatForm({...beatForm, title: e.target.value})} className="bg-black/50 border-white/10" required placeholder="Midnight Drive" data-testid="beat-title-input" /></div>
                      <div className="grid grid-cols-3 gap-3">
                        <div><Label>Genre</Label><Select value={beatForm.genre} onValueChange={(v) => setBeatForm({...beatForm, genre: v})}><SelectTrigger className="bg-black/50 border-white/10"><SelectValue /></SelectTrigger><SelectContent className="bg-zinc-900 border-white/10">{["Trap", "Drill", "R&B", "Boom Bap", "Lo-Fi", "Pop", "Afrobeats", "Reggaeton"].map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent></Select></div>
                        <div><Label>BPM</Label><Input type="number" value={beatForm.bpm} onChange={(e) => setBeatForm({...beatForm, bpm: parseInt(e.target.value) || 140})} className="bg-black/50 border-white/10" /></div>
                        <div><Label>Key</Label><Input value={beatForm.key} onChange={(e) => setBeatForm({...beatForm, key: e.target.value})} className="bg-black/50 border-white/10" /></div>
                      </div>
                      <div><Label>Tags (comma separated)</Label><Input value={beatForm.tags} onChange={(e) => setBeatForm({...beatForm, tags: e.target.value})} className="bg-black/50 border-white/10" placeholder="dark, melodic" /></div>
                      <div className="grid grid-cols-2 gap-3">
                        <div><Label>Basic ($)</Label><Input type="number" step="0.01" value={beatForm.basic_price} onChange={(e) => setBeatForm({...beatForm, basic_price: parseFloat(e.target.value)})} className="bg-black/50 border-white/10" /></div>
                        <div><Label>Premium ($)</Label><Input type="number" step="0.01" value={beatForm.premium_price} onChange={(e) => setBeatForm({...beatForm, premium_price: parseFloat(e.target.value)})} className="bg-black/50 border-white/10" /></div>
                        <div><Label>Unlimited ($)</Label><Input type="number" step="0.01" value={beatForm.unlimited_price} onChange={(e) => setBeatForm({...beatForm, unlimited_price: parseFloat(e.target.value)})} className="bg-black/50 border-white/10" /></div>
                        <div><Label>Exclusive ($)</Label><Input type="number" step="0.01" value={beatForm.exclusive_price} onChange={(e) => setBeatForm({...beatForm, exclusive_price: parseFloat(e.target.value)})} className="bg-black/50 border-white/10" /></div>
                      </div>
                      <Button type="submit" disabled={submitting} className="w-full bg-neon-cyan text-black font-bold" data-testid="submit-beat-form">{submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}Submit for Review</Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {[
                { label: "Submitted", value: stats.total_submitted || 0, color: "text-neon-cyan" },
                { label: "Approved", value: stats.approved || 0, color: "text-neon-green" },
                { label: "Pending", value: stats.pending || 0, color: "text-amber-400" },
                { label: "Revenue Split", value: `${stats.revenue_split || 50}%`, color: "text-neon-magenta" },
              ].map(({ label, value, color }) => (
                <div key={label} className="bg-white/5 p-3 text-center rounded-sm">
                  <p className={`font-heading text-xl ${color}`}>{value}</p>
                  <p className="text-[10px] text-zinc-500 uppercase">{label}</p>
                </div>
              ))}
            </div>
            {/* Beat Submissions */}
            {dashboard?.beats?.length > 0 && (
              <div>
                <h3 className="text-sm uppercase text-zinc-400 mb-3">Your Submissions</h3>
                <div className="space-y-2">
                  {dashboard.beats.map((beat) => (
                    <div key={beat.submission_id} className="bg-white/5 p-3 flex items-center gap-3 rounded-sm" data-testid={`producer-beat-${beat.submission_id}`}>
                      <Music className="w-4 h-4 text-neon-cyan flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm truncate">{beat.title}</p>
                        <p className="text-[10px] text-zinc-600">{beat.genre} | {beat.bpm} BPM</p>
                      </div>
                      <Badge className={beat.status === "approved" ? "bg-neon-green/20 text-neon-green" : beat.status === "pending" ? "bg-amber-500/20 text-amber-400" : "bg-red-500/20 text-red-400"}>{beat.status}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Producer Directory */}
        <div>
          <div className="flex items-center gap-3 mb-6">
            <Users className="w-5 h-5 text-neon-magenta" />
            <h2 className="font-heading text-2xl uppercase">Producer Directory</h2>
            <Badge className="bg-white/5 text-zinc-400 border-white/10 text-xs">{directory.length}</Badge>
          </div>

          {dirLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(3)].map((_, i) => <div key={i} className="aspect-[4/3] bg-zinc-900/50 animate-pulse rounded-sm" />)}
            </div>
          ) : directory.length === 0 ? (
            <div className="glass p-12 text-center">
              <Users className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-500">No producers listed yet. Be the first to apply!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {directory.map((prod, index) => (
                <motion.div
                  key={prod.producer_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.08 }}
                >
                  <Link to={`/producers/${prod.producer_id}`} data-testid={`producer-card-${prod.producer_id}`}>
                    <div className="group glass overflow-hidden card-hover">
                      {/* Banner */}
                      <div className="h-28 overflow-hidden relative">
                        {prod.banner_url ? (
                          <img src={prod.banner_url} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-neon-cyan/20 via-zinc-800 to-neon-magenta/20" />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                      </div>
                      {/* Avatar + info */}
                      <div className="relative -mt-8 px-4 pb-4">
                        <div className="w-14 h-14 rounded-full border-3 border-zinc-900 overflow-hidden bg-zinc-800 mb-3">
                          {prod.avatar_url ? (
                            <img src={prod.avatar_url} alt={prod.artist_name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-neon-cyan/30 to-neon-magenta/30">
                              <span className="font-heading text-lg">{prod.artist_name?.[0]?.toUpperCase()}</span>
                            </div>
                          )}
                        </div>
                        <h3 className="font-heading text-lg uppercase truncate group-hover:text-neon-cyan transition-colors">
                          {prod.artist_name}
                        </h3>
                        {prod.tagline && <p className="text-xs text-zinc-500 truncate">{prod.tagline}</p>}
                        <div className="flex items-center gap-4 mt-3 text-xs text-zinc-500">
                          <span className="flex items-center gap-1"><Music className="w-3 h-3" />{prod.beat_count || 0} beats</span>
                          <span className="flex items-center gap-1"><Play className="w-3 h-3" />{(prod.total_plays || 0).toLocaleString()}</span>
                          <span className="flex items-center gap-1"><Heart className="w-3 h-3 text-red-400" />{prod.total_likes || 0}</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProducerPage;
